import React, { useEffect, useState } from "react";
import { Dropdown, Spinner } from "react-bootstrap";
import AdvanceTable from "components/common/advance-table/AdvanceTable";
import AdvanceTableProvider from "providers/AdvanceTableProvider";
import CardDropdown from "components/common/CardDropdown";
import IconButton from "components/common/IconButton";
import TablePaginationFooter from "components/common/TablePaginationFooter";
import AdvanceTableSearchBox from "components/common/advance-table/AdvanceTableSearchBox";
import WidgetSectionTitle from "widgets/WidgetSectionTitle";
import useAdvanceTable from "hooks/useAdvanceTable";
import { ConfirmDeleteModal } from "components/common/ConfirmDeleteModal";
import useAirports from "hooks/airports/useAirports";
import { toast } from "react-toastify";
import dayjs from "dayjs";
import { Drawer, Form, Input, Select, Button as AntButton, Typography } from "antd";
import {
  ExpandOutlined,
  EditOutlined,
  EnvironmentOutlined,
  NumberOutlined,
  ClockCircleOutlined,
  CheckCircleOutlined,
  CalendarOutlined,
} from "@ant-design/icons";

const { Title, Text } = Typography;

const getAirportCode = (airport) =>
  airport?.iataCode || airport?.code || airport?.airportCode || "-";

const getLocation = (airport) => {
  const city = airport?.city || airport?.municipality || "";
  const country = airport?.country || airport?.countryName || "";
  return [city, country].filter(Boolean).join(", ") || "-";
};

const AirportDetailsDrawer = ({
  visible,
  airport,
  loading,
  onClose,
  onEditAirport,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);

  useEffect(() => {
    if (!visible) {
      setIsExpanded(false);
    }
  }, [visible]);

  const isInactive = airport?.active === false;
  const statusStyle = isInactive
    ? { bg: "#FDECEC", text: "#DC2626", label: "Inactive" }
    : { bg: "#E6F9F0", text: "#059669", label: "Active" };

  return (
    <Drawer
      open={visible}
      onClose={onClose}
      placement="right"
      size={isExpanded ? "100%" : 520}
      zIndex={2000}
      styles={{ padding: 24 }}
      title={null}
      closable={false}
      destroyOnHidden
    >
      <div
        style={{
          maxWidth: isExpanded ? 700 : "100%",
          margin: isExpanded ? "0 auto" : 0,
          fontFamily: "Inter, sans-serif",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: 12,
          }}
        >
          <div style={{ fontSize: 13, color: "#6b7280" }}>
            <span style={{ cursor: "pointer" }}>Airports</span>
            <span style={{ margin: "0 6px" }}>/</span>
            <span style={{ color: "#111827", fontWeight: 500 }}>
              {airport?.name || "Airport Details"}
            </span>
          </div>
          <div style={{ display: "flex", gap: 16 }}>
            <ExpandOutlined
              onClick={() => setIsExpanded((prev) => !prev)}
              style={{ cursor: "pointer" }}
            />
            <EditOutlined
              onClick={() => airport?.id && onEditAirport?.(airport.id)}
              style={{ cursor: "pointer" }}
            />
          </div>
        </div>

        {loading && !airport ? (
          <div className="d-flex justify-content-center py-4">
            <Spinner animation="border" variant="success" />
          </div>
        ) : (
          <>
            <Title level={4} style={{ marginBottom: 8 }}>
              {airport?.name || "Airport Details"}
            </Title>

            <div className="d-flex flex-wrap gap-2 mb-3">
              <span
                className="d-inline-flex align-items-center gap-1 px-2 py-1 rounded-pill fw-medium"
                style={{
                  backgroundColor: "#EEF2FF",
                  color: "#4F46E5",
                  fontSize: "0.75rem",
                }}
              >
                <NumberOutlined />
                {getAirportCode(airport)}
              </span>
              <span
                className="d-inline-flex align-items-center gap-1 px-2 py-1 rounded-pill fw-medium"
                style={{
                  backgroundColor: statusStyle.bg,
                  color: statusStyle.text,
                  fontSize: "0.75rem",
                }}
              >
                <span
                  style={{
                    width: 6,
                    height: 6,
                    borderRadius: "50%",
                    backgroundColor: statusStyle.text,
                  }}
                />
                {statusStyle.label}
              </span>
            </div>

            <div
              style={{
                border: "1px solid #e5e7eb",
                borderRadius: 8,
                background: "#fafafa",
                padding: 14,
                marginBottom: 12,
              }}
            >
              <MetaRow
                icon={<EnvironmentOutlined />}
                label="City"
                value={<Text>{airport?.city || airport?.municipality || "—"}</Text>}
              />
              <MetaRow
                icon={<EnvironmentOutlined />}
                label="State"
                value={<Text>{airport?.state || "—"}</Text>}
              />
              <MetaRow
                icon={<EnvironmentOutlined />}
                label="Country"
                value={<Text>{airport?.country || airport?.countryName || "—"}</Text>}
              />
              <MetaRow
                icon={<EnvironmentOutlined />}
                label="Address"
                value={<Text>{airport?.address || "—"}</Text>}
                isLast
              />
            </div>

            <div
              style={{
                border: "1px solid #e5e7eb",
                borderRadius: 8,
                background: "#fff",
                padding: 14,
                marginBottom: 12,
              }}
            >
              <MetaRow
                icon={<ClockCircleOutlined />}
                label="Timezone"
                value={<Text>{airport?.timezone || "—"}</Text>}
              />
              <MetaRow
                icon={<NumberOutlined />}
                label="Coordinates"
                value={
                  <Text>
                    {airport?.latitude ?? "—"}, {airport?.longitude ?? "—"}
                  </Text>
                }
                isLast
              />
            </div>

            <div
              style={{
                border: "1px solid #e5e7eb",
                borderRadius: 8,
                background: "#fff",
                padding: 14,
              }}
            >
              <MetaRow
                icon={<CalendarOutlined />}
                label="Created At"
                value={
                  <Text>
                    {airport?.createdAt
                      ? dayjs(airport.createdAt).format("MMM D, YYYY HH:mm")
                      : "—"}
                  </Text>
                }
              />
              <MetaRow
                icon={<CalendarOutlined />}
                label="Updated At"
                value={
                  <Text>
                    {airport?.updatedAt
                      ? dayjs(airport.updatedAt).format("MMM D, YYYY HH:mm")
                      : "—"}
                  </Text>
                }
                isLast
              />
            </div>
          </>
        )}
      </div>
    </Drawer>
  );
};

const MetaRow = ({ icon, label, value, isLast = false }) => (
  <div
    style={{
      display: "flex",
      alignItems: "flex-start",
      marginBottom: isLast ? 0 : 12,
    }}
  >
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 8,
        color: "#6b7280",
        fontSize: 13,
        flex: 1,
      }}
    >
      {icon}
      <span>{label}</span>
    </div>
    <div style={{ flex: 2 }}>{value}</div>
  </div>
);

const AirportFormDrawer = ({
  show,
  onClose,
  mode = "create",
  initialValue,
  loading,
  onSubmit,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [countryOptions, setCountryOptions] = useState([]);
  const [timezoneOptions, setTimezoneOptions] = useState([]);
  const [isLoadingCountries, setIsLoadingCountries] = useState(false);
  const [countryLoadFailed, setCountryLoadFailed] = useState(false);
  const [isLoadingTimezones, setIsLoadingTimezones] = useState(false);
  const [timezoneLoadFailed, setTimezoneLoadFailed] = useState(false);
  const [form] = Form.useForm();
  const isEditMode = mode === "edit";

  useEffect(() => {
    if (!show) return;

    form.setFieldsValue({
      name: initialValue?.name || "",
      code:
        initialValue?.iataCode || initialValue?.code || initialValue?.airportCode || "",
      city: initialValue?.city || initialValue?.municipality || "",
      state: initialValue?.state || "",
      country: initialValue?.country || initialValue?.countryName || undefined,
      address: initialValue?.address || "",
      latitude:
        initialValue?.latitude !== undefined && initialValue?.latitude !== null
          ? String(initialValue.latitude)
          : "",
      longitude:
        initialValue?.longitude !== undefined && initialValue?.longitude !== null
          ? String(initialValue.longitude)
          : "",
      timezone: initialValue?.timezone || undefined,
      active: initialValue?.active === false ? "inactive" : "active",
    });
  }, [form, initialValue, show]);

  useEffect(() => {
    if (show) {
      const scrollY = window.scrollY;
      document.body.style.position = "fixed";
      document.body.style.top = `-${scrollY}px`;
      document.body.style.left = "0";
      document.body.style.right = "0";
      document.body.style.overflow = "hidden";

      return () => {
        document.body.style.position = "";
        document.body.style.top = "";
        document.body.style.left = "";
        document.body.style.right = "";
        document.body.style.overflow = "";
        window.scrollTo(0, scrollY);
      };
    }
  }, [show]);

  useEffect(() => {
    if (!show) {
      setIsExpanded(false);
      setIsSubmitting(false);
    }
  }, [show]);

  useEffect(() => {
    let mounted = true;

    const fetchCountries = async () => {
      setIsLoadingCountries(true);
      setCountryLoadFailed(false);
      try {
        const response = await fetch("https://restcountries.com/v3.1/all?fields=name,cca2");
        if (!response.ok) {
          throw new Error("Failed to fetch countries");
        }

        const payload = await response.json();
        if (!mounted || !Array.isArray(payload)) return;

        const options = payload
          .filter((item) => item?.cca2 && item?.name?.common)
          .map((item) => ({
            label: `${item.name.common} (${item.cca2})`,
            value: item.cca2,
          }))
          .sort((a, b) => a.label.localeCompare(b.label));

        setCountryOptions(options);
        setCountryLoadFailed(false);
      } catch (error) {
        if (!mounted) return;
        setCountryLoadFailed(true);
        toast.warning("Could not load country list.", { theme: "colored" });
      } finally {
        if (mounted) setIsLoadingCountries(false);
      }
    };

    const fetchTimezones = async () => {
      setIsLoadingTimezones(true);
      setTimezoneLoadFailed(false);
      try {
        // Free public endpoint that returns IANA timezone ids.
        const response = await fetch("https://time.now/developer/api/timezone");
        if (!response.ok) {
          throw new Error("Failed to fetch timezones");
        }

        const payload = await response.json();
        if (!mounted || !Array.isArray(payload)) return;

        setTimezoneOptions(
          payload.map((timezone) => ({
            label: timezone,
            value: timezone,
          })),
        );
        setTimezoneLoadFailed(false);
      } catch (error) {
        if (!mounted) return;
        setTimezoneLoadFailed(true);
      } finally {
        if (mounted) setIsLoadingTimezones(false);
      }
    };

    fetchCountries();
    fetchTimezones();
    return () => {
      mounted = false;
    };
  }, []);

  const handleSave = async (values) => {
    const hasLatitude = values.latitude !== undefined && values.latitude !== "";
    const hasLongitude = values.longitude !== undefined && values.longitude !== "";

    setIsSubmitting(true);
    try {
      const payload = {
        code: values.code.trim().toUpperCase(),
        name: values.name.trim(),
        city: values.city?.trim() || "",
        state: values.state?.trim() || "",
        country: values.country?.trim() || "",
        address: values.address?.trim() || "",
        timezone: values.timezone?.trim() || "",
        active: values.active !== "inactive",
      };
      const requestPayload = {
        ...payload,
        ...(hasLatitude ? { latitude: Number(values.latitude) } : {}),
        ...(hasLongitude ? { longitude: Number(values.longitude) } : {}),
      };

      await onSubmit(requestPayload);
      form.resetFields();
      onClose();
    } catch (_error) {
      // Error toast is handled centrally in useAirports.
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Drawer
      zIndex={2000}
      placement="right"
      closable={false}
      size={isExpanded ? "large" : 560}
      onClose={onClose}
      open={show}
      destroyOnHidden
      footer={
        <div
          style={{
            textAlign: "right",
            maxWidth: isExpanded ? "100%" : 700,
            margin: isExpanded ? 0 : "0 auto",
            paddingTop: 10,
            paddingBottom: 10,
          }}
        >
          <AntButton style={{ marginRight: 8 }} onClick={onClose} disabled={isSubmitting}>
            Cancel
          </AntButton>
          <AntButton
            type="primary"
            onClick={() => form.submit()}
            loading={isSubmitting || loading}
          >
            {isEditMode ? "Save Changes" : "Create Airport"}
          </AntButton>
        </div>
      }
    >
      <div
        style={{
          maxWidth: isExpanded ? "100%" : 700,
          margin: isExpanded ? 0 : "0 auto",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: 12,
          }}
        >
          <div style={{ fontSize: 15, color: "#6b7280", fontFamily: "Inter" }}>
            <span style={{ cursor: "pointer" }}>Airport</span>
            <span style={{ margin: "0 6px" }}>/</span>
            <span style={{ color: "#111827", fontWeight: 500 }}>
              {isEditMode ? "Edit Airport" : "Create Airport"}
            </span>
          </div>

          <div style={{ display: "flex", gap: 16 }}>
            <ExpandOutlined
              className="task-drawer-icon"
              onClick={() => setIsExpanded((prev) => !prev)}
              style={{ cursor: "pointer" }}
            />
          </div>
        </div>

        <Form
          layout="vertical"
          form={form}
          onFinish={handleSave}
          initialValues={{
            active: "active",
          }}
        >
          <Form.Item
            label="Airport Name"
            name="name"
            rules={[{ required: true, message: "Airport name is required" }]}
          >
            <Input placeholder="Enter airport name" maxLength={120} />
          </Form.Item>

          <Form.Item
            label="Code"
            name="code"
            rules={[{ required: true, message: "Code is required" }]}
          >
            <Input placeholder="e.g. JFK" maxLength={8} />
          </Form.Item>

          <Form.Item label="City" name="city">
            <Input placeholder="City" maxLength={80} />
          </Form.Item>

          <Form.Item label="State" name="state">
            <Input placeholder="e.g. WA" maxLength={40} />
          </Form.Item>

          <Form.Item label="Country" name="country">
            {countryLoadFailed ? (
              <Input placeholder="Enter country code manually (e.g. US)" maxLength={2} />
            ) : (
              <Select
                showSearch
                allowClear
                loading={isLoadingCountries}
                placeholder="Select country"
                optionFilterProp="label"
                options={countryOptions}
              />
            )}
          </Form.Item>

          <Form.Item label="Address" name="address">
            <Input placeholder="Airport address" maxLength={255} />
          </Form.Item>

          <Form.Item
            label="Latitude"
            name="latitude"
            rules={[
              {
                validator: (_, value) => {
                  if (value === undefined || value === null || value === "") return Promise.resolve();
                  return Number.isNaN(Number(value))
                    ? Promise.reject(new Error("Latitude must be a valid number"))
                    : Promise.resolve();
                },
              },
            ]}
          >
            <Input placeholder="e.g. 47.4502" />
          </Form.Item>

          <Form.Item
            label="Longitude"
            name="longitude"
            rules={[
              {
                validator: (_, value) => {
                  if (value === undefined || value === null || value === "") return Promise.resolve();
                  return Number.isNaN(Number(value))
                    ? Promise.reject(new Error("Longitude must be a valid number"))
                    : Promise.resolve();
                },
              },
            ]}
          >
            <Input placeholder="e.g. -122.3088" />
          </Form.Item>

          <Form.Item label="Timezone" name="timezone">
            {timezoneLoadFailed ? (
              <Input placeholder="Enter timezone manually (e.g. America/Chicago)" />
            ) : (
              <Select
                showSearch
                allowClear
                placeholder="Select timezone"
                loading={isLoadingTimezones}
                options={timezoneOptions}
              />
            )}
          </Form.Item>

          <Form.Item label="Status" name="active">
            <Select
              options={[
                { label: "Active", value: "active" },
                { label: "Inactive", value: "inactive" },
              ]}
            />
          </Form.Item>
        </Form>
      </div>
    </Drawer>
  );
};

export const Airports = () => {
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(20);

  const {
    airports,
    selectedAirport,
    loading,
    error,
    totalPages,
    totalElements,
    fetchAirportById,
    createAirport,
    updateAirport,
    deleteAirport,
  } = useAirports(page, pageSize);

  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showFormDrawer, setShowFormDrawer] = useState(false);
  const [formMode, setFormMode] = useState("create");
  const [formAirport, setFormAirport] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [airportToDelete, setAirportToDelete] = useState(null);

  const handleOpenDetails = async (id) => {
    try {
      await fetchAirportById(id);
      setShowDetailsModal(true);
    } catch (_error) {
      // Error toast is handled centrally in useAirports.
    }
  };

  const handleOpenEdit = async (id) => {
    try {
      const airport = await fetchAirportById(id);
      setFormMode("edit");
      setFormAirport(airport);
      setShowFormDrawer(true);
    } catch (_error) {
      // Error toast is handled centrally in useAirports.
    }
  };

  const handleOpenCreate = () => {
    setFormMode("create");
    setFormAirport(null);
    setShowFormDrawer(true);
  };

  const handleSubmitForm = async (payload) => {
    if (formMode === "edit" && formAirport?.id) {
      await updateAirport(formAirport.id, payload);
      return;
    }
    setPage(0);
    await createAirport(payload);
  };

  const columns = [
    {
      accessorKey: "name",
      header: "Airport Name",
      meta: { headerProps: { className: "text-900" } },
    },
    {
      id: "code",
      header: "Code",
      accessorFn: (row) => getAirportCode(row),
      cell: ({ getValue }) => getValue(),
      meta: { headerProps: { className: "text-900" } },
    },
    {
      id: "state",
      header: "State",
      accessorFn: (row) => row.state || "-",
      cell: ({ getValue }) => getValue(),
      meta: { headerProps: { className: "text-900" } },
    },
    {
      id: "location",
      header: "Location",
      accessorFn: (row) => getLocation(row),
      cell: ({ getValue }) => getValue(),
      meta: { headerProps: { className: "text-900" } },
    },
    {
      id: "status",
      header: "Status",
      accessorFn: (row) => (row.active === false ? "Inactive" : "Active"),
      cell: ({ getValue }) => {
        const status = getValue();
        const statusUi =
          status === "Inactive"
            ? { bg: "#FDECEC", text: "#DC2626", label: "Inactive" }
            : { bg: "#E6F9F0", text: "#059669", label: "Active" };

        return (
          <span
            className="d-inline-flex align-items-center gap-1 px-2 py-1 rounded-pill fw-medium"
            style={{
              backgroundColor: statusUi.bg,
              color: statusUi.text,
              fontSize: "0.75rem",
            }}
          >
            <span
              style={{
                width: 6,
                height: 6,
                borderRadius: "50%",
                backgroundColor: statusUi.text,
              }}
            />
            {statusUi.label}
          </span>
        );
      },
      meta: { headerProps: { className: "text-900" } },
    },
    {
      id: "actions",
      header: "Actions",
      cell: ({ row }) => {
        const item = row.original;
        return (
          <CardDropdown drop="start">
            <div className="py-2">
              <Dropdown.Item onClick={() => handleOpenDetails(item.id)}>
                View
              </Dropdown.Item>
              <Dropdown.Item onClick={() => handleOpenEdit(item.id)}>
                Edit
              </Dropdown.Item>
              <Dropdown.Divider as="div" />
              <Dropdown.Item
                onClick={() => {
                  setAirportToDelete(item.id);
                  setShowDeleteModal(true);
                }}
                className="text-danger"
              >
                Delete
              </Dropdown.Item>
            </div>
          </CardDropdown>
        );
      },
      meta: {
        headerProps: { className: "text-900 text-center" },
        cellProps: { className: "text-center" },
      },
    },
  ];

  const table = useAdvanceTable({
    data: airports,
    columns,
    sortable: true,
    pagination: true,
    perPage: 10,
  });

  if (loading && airports.length === 0) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: 400 }}>
        <Spinner animation="border" variant="success" />
        <span className="ms-3" style={{ fontSize: "13.33px" }}>
          Loading...
        </span>
      </div>
    );
  }

  return (
    <>
      <WidgetSectionTitle
        icon="plane-departure"
        title="Airports"
        subtitle="Manage airport records used across operations."
        transform="shrink-2"
        className="mb-4 mt-3"
      />

      <AdvanceTableProvider {...table}>
        <div className="d-flex justify-content-between mb-2 flex-wrap align-items-center">
          <div style={{ width: "100%", maxWidth: 300 }}>
            <AdvanceTableSearchBox placeholder="Search airports..." />
          </div>
          <div className="mt-3 mt-lg-0">
            <IconButton
              variant="falcon-default"
              size="sm"
              icon="plus"
              transform="shrink-3"
              onClick={handleOpenCreate}
              className="text-xs me-3"
            >
              <span className="ms-1">Create Airport</span>
            </IconButton>
          </div>
        </div>
        <AdvanceTable
          headerClassName="bg-300 text-nowrap align-middle"
          rowClassName="align-middle white-space-nowrap"
          tableProps={{
            bordered: true,
            striped: true,
            className: "fs-10 mb-0 overflow-hidden",
          }}
        />
        <div className="mt-3">
          <TablePaginationFooter
            page={page}
            totalPages={totalPages}
            totalElements={totalElements}
            onPrev={() => setPage((prev) => Math.max(prev - 1, 0))}
            onNext={() => setPage((prev) => Math.min(prev + 1, totalPages - 1))}
            pageSize={pageSize}
            onPageSizeChange={(size) => {
              setPageSize(size);
              setPage(0);
            }}
            pageSizeOptions={[10, 20, 50, 100]}
          />
        </div>
      </AdvanceTableProvider>

      <AirportDetailsDrawer
        visible={showDetailsModal}
        onClose={() => setShowDetailsModal(false)}
        airport={selectedAirport}
        loading={loading}
        onEditAirport={(id) => {
          setShowDetailsModal(false);
          handleOpenEdit(id);
        }}
      />

      <AirportFormDrawer
        show={showFormDrawer}
        onClose={() => setShowFormDrawer(false)}
        mode={formMode}
        initialValue={formAirport}
        loading={loading}
        onSubmit={handleSubmitForm}
      />

      <ConfirmDeleteModal
        show={showDeleteModal}
        setShow={setShowDeleteModal}
        onConfirm={() => deleteAirport(airportToDelete)}
        itemName="airport"
        loading={loading}
      />
    </>
  );
};
